import {
    NoMatch as OldNoMatch,
    Page as OldPage,
    PageHeader as OldPageHeader,
    PageContent as OldPageContent,
} from "@edgetier/client-components";
import { Spinner } from "@edgetier/components";
import { NoMatch, Page } from "@edgetier/global-components";
import { WatchtowerVersion } from "@edgetier/watchtower-types";
import VerifyPermission from "components-for/pages/verify-permission";
import VerifyManageTagsPermission from "components-for/tags/verify-manage-tags-permission";
import RoutesHierarchy from "constants/routes";
import { useClient } from "hooks-for/clients/use-client";
import useIsRouteEnabled from "hooks-for/navigation/use-is-route-enabled/use-is-route-enabled";
import { useSchemaContext } from "hooks-for/reskin/use-schema-context";
import { useUser } from "hooks-for/users/use-user";
import { PropsWithChildren, lazy as ReactLazy, ReactNode, Suspense, useContext, useMemo, useState } from "react";
import { LazyRouteFunction, RouteObject } from "react-router-dom";
import {
    IRouteConfig,
    IRouteHandleEnabledValue,
    IRouteHierarchy,
    isIRouteHandleSimplePermission,
} from "types-for/routes";
import { getAccessToken } from "utilities/access-token";
import { importOrReload } from "utilities-for/chunk-load-error/chunk-load-error";

/**
 * Component for checking if a route is enabled based on user access and client settings.
 * @param {object} props - The props for the IsRouteEnabled component.
 * @param {boolean} props.enabled - The route enabled value.
 * @param {ReactNode} props.children - The child components.
 * @returns {JSX.Element} IsRouteEnabled component.
 */
export const IsRouteEnabled = ({
    enabled,
    children,
}: PropsWithChildren<{
    enabled: IRouteHandleEnabledValue;
}>): JSX.Element => {
    const { isV1 } = useSchemaContext();
    const NoMatchToUse = useMemo(() => {
        return isV1 ? <OldNoMatch /> : <NoMatch />;
    }, [isV1]);
    const isRouteEnabled = useIsRouteEnabled();
    const accessToken = useMemo(() => getAccessToken(), []);
    const [hasAccessToken] = useState(typeof accessToken === "string");
    const { data: user } = useUser({
        enabled: hasAccessToken,
    });
    const { data: client } = useClient(user?.clientId!, {
        enabled: typeof user !== "undefined",
    });

    return <>{isRouteEnabled(enabled, client) ? children : <>{NoMatchToUse}</>}</>;
};

const getLazyComponent = (key: string, route: IRouteConfig) => {
    // We extract the lazy prop because we don't want to include it in the route
    const { lazy: lazyProp } = route;
    // LazyProp might be a function or a a record with multiple functions.

    /**
     * Doesn't makes any sense to have a route with a permission defined and an undefined lazy function.
     * So we throw an error to warn the developer.
     */
    if (!lazyProp) throw new Error(`Route ${key} has a permission but no lazy function.`);
    /**
     * React router lazy functions force the component to be exported with the name "Component".
     * The react native lazy function forces the component to be exported with the name "default".
     * So here we wrap the react router lazy function to return a default.
     */
    let adaptedLazyFunction;
    if (typeof lazyProp === "function") {
        adaptedLazyFunction = () => importOrReload(() => lazyProp()).then((module) => ({ default: module.Component! }));
    } else {
        adaptedLazyFunction = Object.keys(lazyProp).reduce(
            (prev, curr) => {
                return {
                    [curr]: () =>
                        importOrReload(() => lazyProp[curr as WatchtowerVersion]()).then((module) => ({
                            default: module.Component!,
                        })),
                    ...prev,
                };
            },
            {} as Record<WatchtowerVersion, () => any>
        );
    }

    // And we create the React.lazy component.
    const LazyComponentPrev:
        | React.LazyExoticComponent<React.ComponentType<any>>
        | Record<WatchtowerVersion, React.LazyExoticComponent<React.ComponentType<any>>> =
        typeof adaptedLazyFunction === "function"
            ? ReactLazy(adaptedLazyFunction)
            : Object.keys(adaptedLazyFunction).reduce(
                  (prev, curr) => {
                      return { [curr]: ReactLazy(adaptedLazyFunction[curr as WatchtowerVersion]), ...prev };
                  },
                  {} as Record<WatchtowerVersion, React.LazyExoticComponent<React.ComponentType<any>>>
              );

    const InnerComponent = () => {
        const { watchtowerVersion, isV1 } = useSchemaContext();
        const LazyComponent = useMemo<React.LazyExoticComponent<React.ComponentType<any>>>(() => {
            if (watchtowerVersion in LazyComponentPrev) {
                return (
                    LazyComponentPrev as Record<WatchtowerVersion, React.LazyExoticComponent<React.ComponentType<any>>>
                )[watchtowerVersion] as React.LazyExoticComponent<React.ComponentType<any>>;
            } else {
                return LazyComponentPrev as React.LazyExoticComponent<React.ComponentType<any>>;
            }
        }, [watchtowerVersion]);

        const fallback = useMemo(() => {
            if (isV1) {
                return (
                    <OldPage className="loading">
                        <OldPageHeader title="Loading..." />
                        <OldPageContent>
                            <Spinner />
                        </OldPageContent>
                    </OldPage>
                );
            } else {
                return (
                    <Page name="loading">
                        <Page.Header title="Loading..." />
                        <Page.Content>
                            <Spinner />
                        </Page.Content>
                    </Page>
                );
            }
        }, [isV1]);

        return (
            <Suspense fallback={fallback}>
                <LazyComponent />
            </Suspense>
        );
    };
    return InnerComponent;
};

/**
 * Internally used function to get the router setup. It gets a routes hierarchy and returns a router setup that works with createBrowserRouter.
 * @param hierarchy The routes hierarchy to get the router setup from. Defaults to the RoutesHierarchy constant.
 * @returns The router setup.
 */
export const getRouterSetup = (hierarchy: IRouteHierarchy = RoutesHierarchy): RouteObject[] => {
    return Object.keys(hierarchy).map<RouteObject>((key) => {
        // First we prepare the route object props we need.

        // First we check if route can be enabled/disabled
        const enabledConfiguration = hierarchy[key].handle?.enabled;

        // Second we check if there's a "permission" property in the route handle section.
        const routePermission = hierarchy[key].handle?.permission;

        // We don't need to check if the permission has the proper type as it is forced in the RouteHierarchy type.
        if (routePermission || enabledConfiguration) {
            const { children, lazy, ...otherPrivateRouteProps } = hierarchy[key];
            const InnerComponent = getLazyComponent(key, hierarchy[key]);

            const WrapperComponent: ReactNode = (
                <ConditionalWrapper
                    condition={!!enabledConfiguration}
                    wrapper={(children) => <IsRouteEnabled enabled={enabledConfiguration!}>{children}</IsRouteEnabled>}
                >
                    <ConditionalWrapper
                        condition={!!routePermission}
                        wrapper={
                            isIRouteHandleSimplePermission(routePermission!)
                                ? (children) => (
                                      <VerifyPermission
                                          name={`conditional router wrapper ${key}`}
                                          permission={routePermission}
                                          isRoute
                                          operation={hierarchy[key]?.handle?.operation}
                                      >
                                          {children}
                                      </VerifyPermission>
                                  )
                                : (children) => (
                                      <VerifyManageTagsPermission name={`conditional router wrapper ${key}`} isRoute>
                                          {children}
                                      </VerifyManageTagsPermission>
                                  )
                        }
                    >
                        <InnerComponent />
                    </ConditionalWrapper>
                </ConditionalWrapper>
            );

            // As we want to verify the permission, we wrap the lazy component inside a VerifyPermission component.
            return {
                id: key,
                element: WrapperComponent,
                ...(children ? { children: getRouterSetup(children) } : {}),
                ...otherPrivateRouteProps,
            } as RouteObject;
        } else {
            const { children, lazy, ...otherPrivateRouteProps } = hierarchy[key];
            // If the component doesn't have a permission defined, we just return it.
            const elementProps: { element?: ReactNode; lazy?: LazyRouteFunction<any> } = {};
            if (lazy) {
                if (typeof lazy === "object") {
                    const InnerComponentDefinition = getLazyComponent(key, hierarchy[key]);
                    const WrapperComponent: ReactNode = <InnerComponentDefinition />;
                    elementProps.element = WrapperComponent;
                } else {
                    elementProps.lazy = () => importOrReload(() => lazy());
                }
            }
            return {
                id: key,
                ...elementProps,
                ...(children ? { children: getRouterSetup(children) } : {}),
                ...otherPrivateRouteProps,
            } as RouteObject;
        }
    });
};

const ConditionalWrapper = ({
    condition,
    wrapper,
    children,
}: PropsWithChildren<{
    condition: boolean;
    wrapper: (children: ReactNode | undefined) => JSX.Element;
}>): JSX.Element => (condition ? wrapper(children) : <>{children}</>);
